import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { useNavigator } from '@repo/toolkit/use-navigator'
import ActionButton from '@repo/ui/action-button'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import { DuplicateIcon, FilterTwoIcon, PlusIcon, UploadIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import React, { useState } from 'react'
import { toast } from 'sonner'

import { API } from '../../../api/apis.config'
import { duplicateOption } from '../../../api/options/duplicate-option'
import CountryFlag from '../../../components/elements/country-flag'
import FilterDrawer from '../../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../../components/elements/dynamic-table'
import { countFilters } from '../../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../../components/elements/nav-bar'
import PeriodBadge from '../../../components/elements/period-badge'
import JourneyPlaceDuplicateOption from '../../../components/pages/journey-places/option/duplicate/journey-place-duplicate-option'
import JourneyPlaceOptionFilter from '../../../components/pages/journey-places/option/journey-place-option-filter'
import { Paths } from '../../../lib/constants/path'
import { useDownloadDocuments } from '../../../lib/hooks/use-download-documents'
import { useFilters } from '../../../lib/hooks/use-filters'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { Option, OptionFilters } from '../../../lib/types/models/options/option'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import { t } from '../../../locale/utils'
import {
  transformOptionFiltersToUrl,
  transformUrlToOptionFilters
} from '../utils/options-filter-mapping'

const JourneyPlaceOptionsPage = () => {
  const tableRef = React.useRef<DynamicTableRef<Option>>(null)
  const { downloadDocuments } = useDownloadDocuments()
  const [isTableLoading, setIsTableLoading] = useState(false)

  const { modalState, toggleModal, closeModel, selectedData } = useModalManager<
    'filters' | 'duplicate',
    Option
  >(['filters', 'duplicate'])

  const { navigate, navigateWithEvent } = useNavigator()

  const { mutateAsync } = useMutation({
    mutationFn: duplicateOption,
    onError: showAxiosError,
    onSuccess() {
      tableRef.current?.refetch()
      toast.success('Option dupliquée avec succès')
      closeModel('duplicate')
    }
  })

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<OptionFilters>({
      initialFilters: {},
      options: { allowSearchParam: true },
      transformFilterToUrl: transformOptionFiltersToUrl,
      transformUrlToFilter: transformUrlToOptionFilters
    })

  const columns: Column<Option>[] = [
    {
      accessorKey: 'code',
      header: 'Code',
      enableSorting: true
    },
    {
      accessorKey: 'title',
      header: 'NOM',
      enableSorting: true,
      cell: (data) => getTranslatedName(data.row.original)
    },
    {
      accessorKey: 'picto',
      header: 'Icône',
      enableSorting: false,
      cell: (data) => {
        if (data.row.original.picto?.path)
          return (
            <img
              src={data.row.original.picto?.path}
              alt={getTranslatedName(data.row.original)}
              className="rounded-full w-8 h-8 object-cover"
            />
          )
        else return '-'
      }
    },
    {
      accessorKey: 'picture',
      header: 'Image',
      enableSorting: false,
      cell: (data) => {
        if (data.row.original.picture?.path)
          return (
            <img
              src={data.row.original.picture?.path}
              alt={getTranslatedName(data.row.original)}
              className="rounded-full w-8 h-8 object-cover"
            />
          )
        else return '-'
      }
    },
    {
      accessorKey: 'prices',
      header: 'Durée(s) en jours',
      enableSorting: false,
      cell: (data) => {
        const duration = [
          ...new Set(data.row.original.prices.flatMap((price) => price.appliedDurationsInDays))
        ]

        return duration?.length > 0 ? (
          <div className="flex gap-2 items-center flex-wrap w-24 py-2">
            {duration?.map((duration, index) => (
              <Badge
                key={duration + index}
                variant="grey"
                textColor={tailwindColors.grey.DEFAULT}
                text={duration?.toString()}
              />
            ))}
          </div>
        ) : (
          '-'
        )
      }
    },
    {
      accessorKey: 'prices',
      header: 'Pays',
      enableSorting: false,
      cell: (data) => {
        // Flatten and remove duplicates based on country name
        const countries = data.row.original.prices
          .flatMap((price) => price.countries)
          .filter(
            (country, index, self) => index === self.findIndex((c) => c.name === country.name)
          )

        return countries?.length > 0 ? (
          <div className="flex gap-1 w-20 flex-wrap">
            {countries?.map((country) => (
              <CountryFlag key={country.code} code={country.code} />
            ))}
          </div>
        ) : (
          '-'
        )
      }
    },

    {
      accessorKey: 'prices',
      header: 'Formules',
      enableSorting: false,
      cell: (data) => {
        // Flatten the formulas and remove duplicates using Set
        const formulas = data.row.original.prices
          .flatMap((price) => price.formulas)
          .filter((formula, index, self) => index === self.findIndex((f) => f.id === formula.id))

        return formulas?.length > 0 ? (
          <div className="flex gap-2 items-center flex-wrap w-52 text-wrap">
            {formulas?.map((formula) => getTranslatedName(formula)).join(', ')}
          </div>
        ) : (
          '-'
        )
      }
    },

    {
      accessorKey: 'prices',
      header: 'Périodes',
      enableSorting: false,
      cell: (data) => {
        // Flatten and remove duplicates based on a unique property, like period.id
        const periods = data.row.original.prices
          .flatMap((price) => price.periods)
          .filter((period, index, self) => index === self.findIndex((p) => p.id === period.id))

        return periods?.length > 0 ? (
          <div className="flex gap-2 items-center flex-wrap w-52">
            {periods?.map((period) => (
              <PeriodBadge key={period?.id} period={period} />
            ))}
          </div>
        ) : (
          '-'
        )
      }
    },
    {
      enableSorting: false,
      header: '',
      accessorKey: 'id',
      cell: ({ row }) => (
        <div className="flex">
          <ActionButton
            icon={DuplicateIcon}
            variant="primary"
            onClick={(e) => {
              e.stopPropagation()
              toggleModal('duplicate', row.original)
            }}
          />
        </div>
      )
    }
  ]

  return (
    <div>
      <UserNavBar
        title="Thèmes et options"
        icon={(props) => <FilterTwoIcon {...props} fill={tailwindColors.secondary.DEFAULT} />}
      />
      <div className="h-4" />
      <DynamicDataTable<Option, OptionFilters>
        ref={tableRef}
        onLoadingChange={setIsTableLoading}
        title="Toutes les options"
        columns={columns}
        deleteMessage={{
          name: 'options',
          attributeName: 'id',
          deleteOne: (option) =>
            `Êtes-vous sûr de vouloir supprimer l'option "${getTranslatedName(option)}" ?`,
          deleteMany: (options) =>
            t('dynamic.data.table.delete.many.label', {
              message: `options "${options.map((option) => getTranslatedName(option)).join('", "')}"`
            })
        }}
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter)}
        onFiltersClick={() => toggleModal('filters')}
        selectable
        allowSearchParam
        endpoint={API.options.getAll.url}
        onRowClick={(row, event) =>
          navigateWithEvent(
            Paths.JOURNEY_PLACE_OPTION_UPDATE_PAGE.replace(':id', row.id.toString()),
            event
          )
        }
        version="FULL"
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            label: 'Ajouter une nouvelle option',
            fn: () => {
              navigate(Paths.JOURNEY_PLACE_OPTION_CREATE_PAGE)
            }
          },
          {
            type: 'multiple',
            icon: UploadIcon,
            refetch: true,
            variant: 'ghost',
            label: 'Exporter',
            fn: (rows) => {
              const ids = rows.map((row) => row.id)
              downloadDocuments({
                endpoint: API.options.exportExcel.url,
                ids
              })
            }
          }
        ]}
      />

      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <JourneyPlaceOptionFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>

      <Modal
        isOpen={modalState.duplicate}
        title={"Dupliquer l'option"}
        icon={FilterTwoIcon}
        size="medium"
        hideActions
        onCancel={() => closeModel('duplicate')}
      >
        <JourneyPlaceDuplicateOption
          className={MODAL_BODY_CLASSNAME}
          onCancel={() => closeModel('duplicate')}
          handleSubmit={async (values) => {
            if (selectedData) {
              await mutateAsync({
                ...values,
                id: selectedData.id
              })
            }
          }}
        />
      </Modal>
    </div>
  )
}

export default JourneyPlaceOptionsPage
